/**
 * The seeded scoring ladder (schema.sql "Seed rows", models.md §13) as data, for "Reset ladder"
 * (`rules.resetLadder`, `models.applyRecommended`). Six rows: Jev's block (positions 0–2) and the on-device
 * model's block (positions 1000–1002), each condition naming the model it tests. They are built from the
 * catalogue's `defaults.ladder`, so the catalogue is the one truth.
 *
 * A fresh database gets them as they are here (schema 1 seeds Jev's three rows without a model, migration 3
 * names the model and adds the on-device rows); a test compares a freshly migrated database with this list.
 */

import type { RuleId } from '../contracts/ids.js';
import type { ModelCatalogEntry, ModelId } from '../contracts/models.js';
import type { GroupNode, RuleAction } from '../contracts/rules.js';
import { MODEL_CATALOG } from './model-catalog.js';

export interface LadderSeedRow {
  id: RuleId;
  name: string;
  icon: string;
  /** The model the row tests. */
  modelId: ModelId;
  /** Seeded evaluation order (Jev 0–2, the on-device model 1000–1002). */
  position: number;
  condition: GroupNode;
  actions: RuleAction[];
}

/** `best-score[model] ≥ value` as a one-condition root group; `minScore` null = "this model scored it" (≥ 0). */
export function bestScoreAtLeast(modelId: ModelId, minScore: number | null): GroupNode {
  return { type: 'group', operator: 'all', children: [{ type: 'condition', field: 'best-score', operator: 'gte', value: minScore ?? 0, model: modelId }] };
}

/** First seeded position of each catalogue entry's ladder block. */
const BLOCK_START: Readonly<Record<string, number>> = { jev: 0, 'e5-small': 1000 };

function ladderOf(entry: ModelCatalogEntry, start: number): LadderSeedRow[] {
  return entry.defaults.ladder.map((row, i) => ({
    id: row.ruleId,
    name: row.name,
    icon: row.icon,
    modelId: entry.id,
    position: start + i,
    condition: bestScoreAtLeast(entry.id, row.minScore),
    actions: [row.action],
  }));
}

/** In evaluation order: Jev's Strong / Loose / Below the bar, then the on-device model's three. */
export const SCORING_LADDER_SEED: readonly LadderSeedRow[] = MODEL_CATALOG.entries.flatMap((entry, i) => ladderOf(entry, BLOCK_START[entry.id] ?? 1000 * i));
